// The band's transport bar — chips (tempo / repeats / style),
// the play cluster and the instrument mixer — over a BandArrangement.
// Extracted from SequenceBuilderPage (unification Phase 2) so the Score page
// mounts the identical transport: one player experience everywhere. All
// control state lives in the store; the arrangement facts come in as the
// `band` prop (ONE useBandArrangement instance per page, shared with its
// view), so the bar itself stays a pure consumer.

import { useMemo, type ReactNode } from 'react';
import { BookOpen, Drum, Metronome, Timer } from 'lucide-react';
import { passageLabel } from '@/music/passage';
import { COMP_STYLES, type CompStyle } from '@/music/comping';
import { PIANO_COMP_FAMILIES, type PianoCompFamily } from '@/music/pianoComping';
import { effectivePianoFamily, PIANO_LEFT_HAND_FAMILIES } from '@/music/pianoLeftHand';
import type { CompInstrument } from '@/music/style';
import { BASS_SOUNDS, COMP_SOUNDS, LEAD_SOUNDS } from './soundChoices';
import type { BassSound, GuitarSound, LeadSound } from '@/audio/instrumentSamples';
import { pitchClassOf } from '@/music/notes';
import { BASS_STYLES } from '@/music/walkingBass';
import {
  GROOVE_KEYS,
  STYLE_MODIFIED,
  STYLE_PRESETS,
  describeBandPreset,
  type StyleBand,
  type StyleId,
} from '@/music/style';
import { feelsInScore } from '@/music/arrangement';
import { catalogueArrangementFor } from '@/storage/grooveCatalogue';
import { hasCatalogueCompPattern } from '@/storage/compPatternCatalogue';
import type { ArrangementPolicy } from '@/music/performance';
import {
  RESOLVE_ENDINGS,
  RITARDANDO_BARS,
  TAG_BARS,
  TAG_TIMES,
  type EndingOptions,
  type ResolveEnding,
} from '@/audio/endings';

/** The Resolve row's buttons: where the last pass lands (#429). */
const RESOLVE_LABELS: Record<ResolveEnding, string> = {
  off: 'Off',
  home: 'Home',
  'flat-six': '♭VI–I',
  'flat-two': '♭II–I',
  top: 'Top',
};
import { SCORE_EDITIONS, scoreEditionDef, type ScoreEdition } from '@/music/score/edition';
import { tuneSectionsOf, useStore } from '@/state/store';
import type { FeelTiming } from '@/audio/feel';
import {
  KIT_FEEL,
  kitSwingRatio,
  swingGrid,
  swingPatch,
  swingRatioRange,
  type SwingChoice,
  type SwingPart,
} from '@/music/swingFeel';
import type { BassAccent, MelodyPhrasing, Placement } from '@/music/bandFeel';
import { kitSwingStatus, shownSwingChoice } from '@/components/swingChoices';
import { useLoopSwing } from '@/audio/useLoopSwing';
import { SwingFeelPicker, type SwingAmount } from '@/components/SwingFeelPicker';
import { SwingRatioSlider } from '@/components/SwingRatioSlider';
import { LevelFader, MixerSoundControl, SegmentedChoice } from '@/components/MixerControls';
import {
  CLICK_ACCENT_CHOICES,
  CLICK_ROUTING_CHOICES,
  CLICK_SOUND_CHOICES,
  CLICK_SUBDIVISION_CHOICES,
} from '@/components/clickChoices';
import type { BandArrangement } from '@/audio/useBandArrangement';
import { getChordPlayer, type TransportState } from '@/audio/player';
import { usePendingChorus } from '@/audio/usePlaybackBeat';
import { chorusLabels } from '@/components/chorusLabels';
import { formatRate, RATE_SLIDER_MAX, rateToSlider, sliderToRate } from '@/components/rateSlider';
import { trainerPassesToTarget, type TrainerConfig } from '@/audio/practice';
import {
  TransportBar,
  TransportControlButton,
  MixerGlyph,
  type TransportPanel,
} from '@/components/TransportBar';
import { NumberField } from '@/components/NumberField';
import {
  DrumLoopMenu,
  DrumLoopRate,
  DrumTimeStretch,
  FormField,
  MatchLoopButton,
  TempoStepper,
} from '@/components/PracticeControls';
import { TuneAbout } from '@/components/TuneAbout';
import { ReharmLayersPanel } from '@/components/ReharmLayersPanel';
import { SectionPicker } from '@/components/SectionPicker';
import { BandScopeRow } from '@/components/BandScopeRow';
import { BandMixer, MixerSettings } from '@/components/BandMixer';
import { useBandEditor } from '@/components/useBandEditor';
import { activeReharmLayer } from '@/music/score/reharm';
import { recordFeature } from '@/analytics/usage';
import { ToggleSwitch } from './ToggleSwitch';
import { DRUM_LOOP_SEARCH, drumLoopOptions } from './drumLoopOptions';
import './BandTransport.css';

export interface BandPlaybackOverride {
  transport: TransportState;
  onPlay: () => void;
  onPause: () => void;
  onResume: () => void;
  onStop: () => void;
}

interface BandTransportProps {
  band: BandArrangement;
  /** Override the band's Play entry point while retaining its normal transport UI. */
  onPlay?: () => void;
  /** False on the Score studio: the reading-edition row renders disabled with
   *  a note, because the studio edits the concert truth and does not read in
   *  an edition (yet) — hidden, the option would read as not existing. */
  readingEditions?: boolean;
  /** Studio edits save the base tempo as part of the written score. */
  editScoreTempo?: boolean;
  /** Keep score setup controls available before the first note is entered. */
  alwaysVisible?: boolean;
  playback?: BandPlaybackOverride;
  playbackSelector?: ReactNode;
}

export function BandTransport({
  band,
  onPlay,
  readingEditions = true,
  editScoreTempo = false,
  alwaysVisible = false,
  playback,
  playbackSelector,
}: BandTransportProps) {
  const audioEnabled = useStore((s) => s.audioEnabled);
  const score = useStore((s) => s.score);
  const passage = useStore((s) => s.passage);
  const clearPassage = useStore((s) => s.clearPassage);
  const loopLabel = passage
    ? passageLabel(
        score.form.map((measure) => measure.id),
        passage,
      )
    : null;
  const tempo = useStore((s) => s.tempo);
  const setTempo = useStore((s) => s.setTempo);
  const setScoreTempo = useStore((s) => s.setScoreTempo);
  const metronome = useStore((s) => s.metronome);
  const setMetronome = useStore((s) => s.setMetronome);
  const metronomeSettings = useStore((s) => s.metronomeSettings);
  const patchMetronome = useStore((s) => s.patchMetronome);
  const masterReverb = useStore((s) => s.masterReverb);
  const setMasterReverb = useStore((s) => s.setMasterReverb);
  const masterReverbEnabled = useStore((s) => s.masterReverbEnabled);
  const setMasterReverbEnabled = useStore((s) => s.setMasterReverbEnabled);
  const masterCompression = useStore((s) => s.masterCompression);
  const setMasterCompression = useStore((s) => s.setMasterCompression);
  const masterCompressionEnabled = useStore((s) => s.masterCompressionEnabled);
  const setMasterCompressionEnabled = useStore((s) => s.setMasterCompressionEnabled);
  const transposeToKey = useStore((s) => s.transposeToKey);
  const shiftOctave = useStore((s) => s.shiftOctave);
  const scoreEdition = useStore((s) => s.scoreEdition);
  const setScoreEdition = useStore((s) => s.setScoreEdition);
  const countIn = useStore((s) => s.countIn);
  const setCountIn = useStore((s) => s.setCountIn);
  const countInBars = useStore((s) => s.countInBars);
  const setCountInBars = useStore((s) => s.setCountInBars);
  const countInBetweenLoops = useStore((s) => s.countInBetweenLoops);
  const setCountInBetweenLoops = useStore((s) => s.setCountInBetweenLoops);
  const speedPct = useStore((s) => s.speedPct);
  const setSpeedPct = useStore((s) => s.setSpeedPct);
  const trainer = useStore((s) => s.trainer);
  const patchTrainer = useStore((s) => s.patchTrainer);
  const repeatForm = useStore((s) => s.repeatForm);
  const arrangementPolicy = useStore((s) => s.arrangementPolicy);
  const setArrangementPolicy = useStore((s) => s.setArrangementPolicy);
  const setRepeatForm = useStore((s) => s.setRepeatForm);
  // The band (bass / comp / drums) is shown and edited through the band
  // editor: the tune's band (remembered per tune by patchBand), or, while the
  // band is split by section or written feel, the band the scope row names
  // (components/bandScope.ts). A preset is applied the same way.
  const editor = useBandEditor(band.bandSplit);
  const patchBand = editor.edit;
  const applyStyle = editor.applyPreset;
  const varies = editor.varies;
  const {
    compStyle,
    compInstrument,
    compPianoFamily,
    compAmount,
    drums,
    bassline,
    bassSolo,
    bassStyle,
    bassFeel,
    bassFollowForm,
    bassGhosts,
    bassAnticipate,
    bassTriplets,
    bassSkips,
    bassAmount,
    bassPlacement,
    bassAccent,
    compPlacement,
    melodyPlacement,
    melodyPhrasing,
    swingOffset,
    melodySwingOffset,
    compSwingOffset,
    bassSwingOffset,
    drumLoopKey,
  } = editor.band;
  const drumTimeStretch = useStore((s) => s.drumTimeStretch);
  const melodyChannel = useStore((s) => s.melodyChannel);
  const setMelodyChannel = useStore((s) => s.setMelodyChannel);
  const leadSound = useStore((s) => s.leadSound);
  const setLeadSound = useStore((s) => s.setLeadSound);
  const bassSound = useStore((s) => s.bassSound);
  const setBassSound = useStore((s) => s.setBassSound);
  const guitarSound = useStore((s) => s.guitarSound);
  const setGuitarSound = useStore((s) => s.setGuitarSound);

  const {
    chart,
    transport,
    playAll,
    entryBars,
    exitBars,
    ending,
    patchEnding,
    endingAvailable,
    currentPass,
    activePct,
    effectiveTempo,
    repeatCount,
    repeatTotal,
    setRepeats,
    currentStyleId,
    currentStyleLabel,
    currentStyleModified,
    beatsPerBar,
    hasMelody,
    melodyInstrument,
    compHasSource,
    bassHasSource,
    meterDrumLoops,
    showTransportBar,
    chorusSkipAvailable,
    chorusKinds,
    statusLine,
    bandSplit,
    playingBandLabel,
  } = band;
  // The kit the controls show: the scoped band's, found the way the band hook
  // finds the live one (the meter's first loop when the key is not offered).
  const activeDrumLoop = !varies.has('drumLoopKey')
    ? (meterDrumLoops.find((l) => l.key === drumLoopKey) ?? meterDrumLoops[0])
    : undefined;
  // What the Kit swing follows: the scoped band's loop, while its drums play.
  const activeLoopSwing = useLoopSwing(activeDrumLoop);
  const swingChoice = shownSwingChoice(editor.band, varies);
  const kitVaries = varies.has('drumLoopKey') || varies.has('drums');
  const kitStatus = kitVaries
    ? null
    : kitSwingStatus(swingChoice, drums && activeDrumLoop ? { swing: activeLoopSwing } : null);
  // The swing sliders under Kit: the band's against its kit, each part's
  // against the band (swingFeel.ts `kitSwingRatio`), the triplet standing in
  // for a kit with no clear skip note. None while the bands being edited follow
  // different kits, where no one ratio is the kit's.
  const kitRatio =
    drums && activeDrumLoop && typeof activeLoopSwing === 'number'
      ? activeLoopSwing
      : swingGrid(KIT_FEEL).ratio;
  const kitSwing =
    swingChoice === 'kit' && !kitVaries
      ? {
          range: swingRatioRange(kitRatio),
          band: kitSwingRatio(kitRatio, { band: swingOffset, melody: 0, comp: 0, bass: 0 }),
        }
      : null;
  const bandSwingAmount: SwingAmount | null = kitSwing
    ? {
        kit: kitRatio,
        range: kitSwing.range,
        offset: swingOffset,
        onChange: (offset) => patchBand({ swingOffset: offset }),
      }
    : null;
  const partSwingField = (part: SwingPart, offset: number) =>
    kitSwing ? (
      <div className="mixer-quick-field" data-testid={`${part}-swing-field`}>
        <span className="label" style={{ textTransform: 'none' }}>
          Swing
        </span>
        <SwingRatioSlider
          base={kitSwing.band}
          range={kitSwing.range}
          offset={offset}
          onChange={(next) => patchBand({ [SWING_OFFSET_FIELD[part]]: next })}
          label={`${SWING_PART_NAMES[part]} swing`}
          testid={`${part}-swing`}
        />
      </div>
    ) : null;
  // The kit's list for the Mixer strip's name, as every loop picker lists it.
  const drumLoopRows = useMemo(() => drumLoopOptions(meterDrumLoops), [meterDrumLoops]);
  // The Style chip names the split while there is one: no single preset is
  // what the whole tune plays.
  const scopedStyle = bandSplit ? describeBandPreset(editor.band) : null;
  const stylesVary = [...varies].some((key) => (GROOVE_KEYS as readonly string[]).includes(key));
  const styleId = bandSplit ? (stylesVary ? null : (scopedStyle?.id ?? null)) : currentStyleId;
  const styleModified = bandSplit ? !stylesVary && !!scopedStyle?.modified : currentStyleModified;
  const styleChipLabel = bandSplit
    ? bandSplit.kind === 'sections'
      ? 'By section'
      : hasCatalogueCompPattern(score)
        ? 'Arrangement'
        : 'Written feels'
    : currentStyleLabel;
  const scopeRow = (withOneBand: boolean, testid?: string) => (
    <BandScopeRow
      editor={editor}
      playingLabel={playingBandLabel}
      withOneBand={withOneBand}
      testid={testid}
    />
  );
  const pendingChorus = usePendingChorus();

  if (!showTransportBar && !alwaysVisible) return null;

  const lastChorus = typeof repeatTotal === 'number' ? repeatTotal : Infinity;
  const skip =
    !playback && chorusSkipAvailable
      ? {
          onBack: () => getChordPlayer().skipChorus(-1),
          onForward: () => getChordPlayer().skipChorus(1),
          onForwardHold: Number.isFinite(lastChorus)
            ? () => getChordPlayer().jumpToChorus(lastChorus)
            : undefined,
          canForward: (pendingChorus ?? currentPass) < lastChorus,
        }
      : undefined;
  // The jump row: every chorus of a finite performance, named by its job.
  const choruses =
    skip && Number.isFinite(lastChorus) && lastChorus > 1
      ? chorusLabels(lastChorus, chorusKinds)
      : null;

  // What the Tempo panel and the mixer's Match loop both read: the preview
  // player's practice speed, the tempo it writes, and the trainer's lock.
  const panelActivePct = playback ? speedPct : activePct;
  const panelEffectiveTempo = playback
    ? Math.max(20, Math.round((tempo * speedPct) / 100))
    : effectiveTempo;
  const panelSetTempo = editScoreTempo ? setScoreTempo : setTempo;
  const trainerLocksTempo = trainer.enabled && !playback;

  // Resolve the saved loop even while the channel is off so its controls
  // remain editable. The Tempo panel still appears only for enabled drums.
  const drumLoopPicker = (
    <>
      {meterDrumLoops.length === 0 ? (
        <span className="muted" style={{ fontSize: 13 }}>
          no loops for a {beatsPerBar}-beat bar
        </span>
      ) : (
        <DrumLoopMenu
          loops={meterDrumLoops}
          value={activeDrumLoop?.key ?? null}
          placeholder={varies.has('drumLoopKey') ? 'Varies' : undefined}
          onChange={(key) => key && patchBand({ drumLoopKey: key })}
          className="band-mixer-sound-select"
        />
      )}
    </>
  );
  const drumLoopSettings = (withMatch: boolean) => (
    <>
      {withMatch && activeDrumLoop && (
        <MatchLoopButton
          loopBpm={activeDrumLoop.bpm}
          effectiveTempo={panelEffectiveTempo}
          activePct={panelActivePct}
          disabled={trainerLocksTempo}
          setTempo={panelSetTempo}
          testid="mixer-match-loop"
        />
      )}
      {activeDrumLoop && (
        <span
          className="muted drum-loop-speed"
          style={{ fontSize: 12, fontVariantNumeric: 'tabular-nums' }}
          title={
            drumTimeStretch
              ? 'The loop is time-stretched from its native tempo; near 100% sounds most natural'
              : 'The loop speeds up or slows down, changing its pitch with the tempo'
          }
        >
          {withMatch && `Playback ♩=${panelEffectiveTempo} · native ♩=${activeDrumLoop.bpm} · `}
          {Math.round((panelEffectiveTempo / activeDrumLoop.bpm) * 100)}% speed
        </span>
      )}
      <DrumTimeStretch />
    </>
  );

  return (
    <TransportBar
      transport={playback?.transport ?? transport}
      audioEnabled={audioEnabled}
      onPlay={playback?.onPlay ?? onPlay ?? playAll}
      onPause={playback?.onPause}
      onResume={playback?.onResume}
      onStop={playback?.onStop}
      looping={!playback && !!loopLabel}
      loopLabel={playback ? null : loopLabel}
      onClearLoop={playback ? undefined : clearPassage}
      startBar={playback ? null : (band.startCursor?.bar ?? null)}
      onClearStart={playback ? undefined : band.clearStartCursor}
      status={playback || transport === 'idle' ? undefined : statusLine}
      skip={skip}
      actions={
        playback
          ? []
          : [
              {
                id: 'metronome',
                label: 'Metronome',
                glyph: <Metronome size={20} strokeWidth={1.8} aria-hidden="true" />,
                pressed: metronome,
                onClick: () => setMetronome(!metronome),
                testId: 'transport-metronome',
              },
              {
                id: 'count-in',
                label: `Count-in (${countInBars} bar${countInBars === 1 ? '' : 's'})`,
                glyph: <Timer size={20} strokeWidth={1.8} aria-hidden="true" />,
                pressed: countIn,
                onClick: () => setCountIn(!countIn),
                testId: 'transport-count-in',
              },
            ]
      }
      panels={buildTransportPanels({
        tempoDenominator: band.chart.timeSignature[1],
        tempo,
        activePct: panelActivePct,
        effectiveTempo: panelEffectiveTempo,
        previewPlayback: !!playback,
        speedPct,
        setSpeedPct,
        trainer,
        patchTrainer,
        countInBars,
        setCountInBars,
        countInBetweenLoops,
        setCountInBetweenLoops,
        setTempo: panelSetTempo,
        tempoLabel: editScoreTempo ? 'Written tempo' : 'Base tempo',
        repeatCount,
        repeatForm,
        setRepeatForm,
        setRepeats,
        arrangementPolicy,
        hasMelody,
        entryBars,
        exitBars,
        ending,
        patchEnding,
        endingAvailable,
        setArrangementPolicy,
        currentPass,
        pendingChorus: playback ? null : pendingChorus,
        choruses,
        repeatTotal,
        currentStyleId: styleId,
        currentStyleLabel: styleChipLabel,
        styleModified,
        stylesVary: !!bandSplit && stylesVary,
        styleScope: scopeRow(false, 'style-scope'),
        applyStyle,
        keyLabel: chart.key,
        transposeToKey,
        shiftOctave,
        octaveShift: score.octaveShift ?? 0,
        reharmLayerName: activeReharmLayer(score)?.name ?? null,
        scoreEdition,
        setScoreEdition,
        readingEditions,
        drums:
          !playback && drums
            ? {
                controls: (
                  <>
                    {drumLoopPicker}
                    {drumLoopSettings(false)}
                  </>
                ),
                loopBpm: activeDrumLoop?.bpm ?? null,
              }
            : null,
        swing: {
          choice: swingChoice,
          onChange: (choice: SwingChoice) => patchBand(swingPatch(choice)),
          scope: playback ? null : scopeRow(false, 'tempo-scope'),
          kitStatus: playback ? null : kitStatus,
          amount: playback ? null : bandSwingAmount,
        },
        bandAndMixer: (panel) => (
          <BandMixer
            panel={panel}
            lineup={
              <div className="mixer-channel mixer-bands" data-testid="band-presets">
                <div className="band-lineup" role="group" aria-label="Lineup">
                  {BAND_LINEUPS.map((lineup) => {
                    const active =
                      !varies.has('compInstrument') &&
                      leadSound === lineup.lead &&
                      compInstrument === lineup.comp &&
                      bassSound === lineup.bass &&
                      !bassSolo &&
                      bassline &&
                      drums;
                    return (
                      <button
                        key={lineup.id}
                        type="button"
                        className={`btn${active ? ' active' : ''}`}
                        aria-pressed={active}
                        data-testid={`band-${lineup.id}`}
                        onClick={() => {
                          setLeadSound(lineup.lead);
                          setBassSound(lineup.bass);
                          if (hasMelody) setMelodyChannel(true);
                          patchBand({
                            compInstrument: lineup.comp,
                            bassSolo: false,
                            bassline: true,
                            drums: true,
                          });
                        }}
                      >
                        {lineup.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            }
            channels={[
              {
                id: 'melody',
                label: 'Melody',
                sound: !hasMelody
                  ? 'No written part'
                  : melodyInstrument === 'bass'
                    ? `Bass · ${BASS_SOUNDS.find((s) => s.id === bassSound)?.label}`
                    : melodyInstrument === 'lead'
                      ? (LEAD_SOUNDS.find((s) => s.id === leadSound)?.label ?? leadSound)
                      : 'Written part',
                picker:
                  melodyInstrument === 'lead'
                    ? {
                        label: 'Melody sound',
                        value: leadSound,
                        options: LEAD_SOUNDS,
                        onChange: (value) => setLeadSound(value as LeadSound),
                      }
                    : melodyInstrument === 'bass'
                      ? {
                          label: 'Written bass sound',
                          value: bassSound,
                          options: BASS_SOUNDS,
                          onChange: (value) => setBassSound(value as BassSound),
                        }
                      : undefined,
                enabled: melodyChannel,
                available: hasMelody,
                unavailableReason: 'No melody',
                onEnabledChange: setMelodyChannel,
              },
              {
                id: 'comp',
                label: 'Comp',
                sound: varies.has('compInstrument')
                  ? 'Varies'
                  : (COMP_SOUNDS.find((s) => s.id === compInstrument)?.label ?? compInstrument),
                picker: {
                  label: 'Comp sound',
                  value: varies.has('compInstrument') ? '' : compInstrument,
                  options: COMP_SOUNDS,
                  onChange: (value) => patchBand({ compInstrument: value as CompInstrument }),
                },
                enabled: !bassSolo,
                available: compHasSource,
                unavailableReason: 'No chords',
                varies: varies.has('bassSolo'),
                onEnabledChange: (enabled) => patchBand({ bassSolo: !enabled }),
              },
              {
                id: 'bass',
                label: 'Bass',
                sound: BASS_SOUNDS.find((s) => s.id === bassSound)?.label ?? bassSound,
                picker: {
                  label: 'Bass sound',
                  value: bassSound,
                  options: BASS_SOUNDS,
                  onChange: (value) => setBassSound(value as BassSound),
                },
                enabled: bassline,
                available: bassHasSource,
                unavailableReason: 'No chords',
                varies: varies.has('bassline'),
                onEnabledChange: (enabled) => patchBand({ bassline: enabled }),
              },
              {
                id: 'drums',
                label: 'Drums',
                sound: varies.has('drumLoopKey') ? 'Varies' : (activeDrumLoop?.label ?? 'No loop'),
                picker: {
                  label: 'Drum loop',
                  value: varies.has('drumLoopKey') ? '' : (activeDrumLoop?.key ?? ''),
                  options: drumLoopRows,
                  onChange: (key) => patchBand({ drumLoopKey: key }),
                  search: DRUM_LOOP_SEARCH,
                  keepOpen: true,
                },
                enabled: drums,
                available: meterDrumLoops.length > 0,
                unavailableReason: 'No loop',
                varies: varies.has('drums'),
                onEnabledChange: (enabled) => patchBand({ drums: enabled }),
              },
            ]}
            source={
              playbackSelector && (
                <div className="mixer-channel mixer-playback-source">{playbackSelector}</div>
              )
            }
            scope={!playback && bandSplit ? scopeRow(true) : null}
            playingSettings={
              <>
                <MixerSettings channel="melody">
                  <div className="mixer-channel mixer-instrument-settings">
                    <label
                      className="switch mixer-toggle"
                      title={
                        hasMelody
                          ? 'Play the written melody line over the band'
                          : 'This tune has no melody — add one on the Score page'
                      }
                    >
                      <input
                        type="checkbox"
                        checked={melodyChannel && hasMelody}
                        disabled={!hasMelody}
                        onChange={(e) => setMelodyChannel(e.target.checked)}
                        data-testid="melody-channel"
                      />
                      Melody
                      {hasMelody && melodyInstrument === 'bass' && (
                        <span className="mixer-part-name" aria-hidden="true">
                          Bass
                        </span>
                      )}
                    </label>
                    {hasMelody && melodyInstrument === 'lead' && (
                      <SoundPicker
                        quick
                        label="Melody sound"
                        value={leadSound}
                        options={LEAD_SOUNDS}
                        onChange={setLeadSound}
                        testid="mixer-quick-melody"
                      />
                    )}
                    {hasMelody && melodyInstrument === 'bass' && (
                      <SoundPicker
                        quick
                        label="Written bass sound"
                        value={bassSound}
                        options={BASS_SOUNDS}
                        onChange={setBassSound}
                        testid="mixer-quick-melody"
                      />
                    )}
                    {hasMelody && melodyInstrument === 'lead' && leadSound === 'guitar' && (
                      <div className="band-channel-details">
                        <div className="mixer-controls">
                          <label
                            className="label"
                            style={{ textTransform: 'none' }}
                            htmlFor="lead-guitar-sound"
                          >
                            Guitar sound
                          </label>
                          <select
                            id="lead-guitar-sound"
                            className="bass-style-select"
                            value={guitarSound}
                            onChange={(e) => setGuitarSound(e.target.value as GuitarSound)}
                            data-testid="lead-guitar-sound"
                          >
                            <option value="jazz-guitar">Jazz guitar</option>
                            <option value="original">Original guitar</option>
                          </select>
                        </div>
                      </div>
                    )}
                    {hasMelody && (
                      <div className="mixer-quick-controls" data-testid="melody-feel">
                        <div className="mixer-quick-field">
                          <span className="label" style={{ textTransform: 'none' }}>
                            Placement
                          </span>
                          <SegmentedChoice
                            label="Melody placement"
                            value={varies.has('melodyPlacement') ? null : melodyPlacement}
                            options={PLACEMENT_CHOICES}
                            onChange={(value) => patchBand({ melodyPlacement: value })}
                            testid="melody-placement"
                          />
                        </div>
                        {partSwingField('melody', melodySwingOffset)}
                        <div className="mixer-quick-field">
                          <span className="label" style={{ textTransform: 'none' }}>
                            Phrasing
                          </span>
                          <SegmentedChoice
                            label="Melody phrasing"
                            value={varies.has('melodyPhrasing') ? null : melodyPhrasing}
                            options={PHRASING_CHOICES}
                            onChange={(value) => patchBand({ melodyPhrasing: value })}
                            testid="melody-phrasing"
                          />
                        </div>
                      </div>
                    )}
                    {hasMelody && melodyInstrument === 'bass' && (
                      <span className="band-channel-note mixer-channel-note">
                        Bass sound is shared with bass accompaniment.
                      </span>
                    )}
                    {hasMelody && melodyInstrument !== 'lead' && melodyInstrument !== 'bass' && (
                      <span className="band-channel-note">Uses the written instrument</span>
                    )}
                    {!hasMelody && (
                      <div className="mixer-controls">
                        <span className="label" style={{ textTransform: 'none', opacity: 0.6 }}>
                          No melody in this tune
                        </span>
                      </div>
                    )}
                  </div>
                </MixerSettings>
                <MixerSettings channel="comp">
                  <div className="mixer-channel mixer-instrument-settings">
                    <label
                      className="switch mixer-toggle"
                      title={
                        compHasSource
                          ? 'Play the chord comp — turn off to hear only bass / drums'
                          : 'This tune has no chords, so there is nothing to comp'
                      }
                    >
                      <input
                        type="checkbox"
                        checked={!bassSolo}
                        disabled={!compHasSource}
                        onChange={(e) => patchBand({ bassSolo: !e.target.checked })}
                        data-testid="mix-guitar"
                      />
                      Comp
                    </label>
                    {compHasSource && (
                      <SoundPicker
                        quick
                        label="Comp sound"
                        value={
                          varies.has('compInstrument') ? ('' as CompInstrument) : compInstrument
                        }
                        options={COMP_SOUNDS}
                        onChange={(value) => patchBand({ compInstrument: value })}
                        testid="mixer-quick-comp"
                      />
                    )}
                    {!compHasSource ? (
                      <div className="mixer-controls">
                        <span
                          className="label"
                          style={{ textTransform: 'none', opacity: 0.6 }}
                          data-testid="comp-no-source"
                        >
                          No chords to comp
                        </span>
                      </div>
                    ) : (
                      <div className="mixer-quick-controls">
                        <div className="mixer-quick-field">
                          <span
                            className="label"
                            style={{ textTransform: 'none' }}
                            title="How the chords comp during playback (the sheets stay as written)"
                          >
                            Rhythm
                          </span>
                          <select
                            className="bass-style-select"
                            value={varies.has('compStyle') ? '' : compStyle}
                            onChange={(e) => patchBand({ compStyle: e.target.value as CompStyle })}
                            title={COMP_STYLES.find((s) => s.id === compStyle)?.hint}
                            aria-label="Comping style"
                            data-testid="comp-style"
                          >
                            <VariesOption />
                            {COMP_STYLES.map((s) => (
                              <option key={s.id} value={s.id}>
                                {s.label}
                              </option>
                            ))}
                          </select>
                        </div>

                        {(compStyle === 'freestyle' ||
                          compStyle === 'interactive' ||
                          compStyle === 'beat-two' ||
                          compStyle === 'funk' ||
                          compStyle === 'sparse') && (
                          <div className="mixer-quick-field mixer-quick-field--activity">
                            <span className="label" style={{ textTransform: 'none' }}>
                              Activity
                            </span>
                            <input
                              type="range"
                              min={0}
                              max={100}
                              step={5}
                              value={Math.round(compAmount * 100)}
                              onChange={(e) =>
                                patchBand({ compAmount: Number(e.target.value) / 100 })
                              }
                              aria-label={
                                compStyle === 'interactive' ||
                                compStyle === 'funk' ||
                                compStyle === 'beat-two'
                                  ? 'Comping activity'
                                  : compStyle === 'sparse'
                                    ? 'Embellishment rate'
                                    : 'Approach-stab density'
                              }
                              data-testid="comp-amount"
                              title={
                                compStyle === 'funk'
                                  ? 'Adds offbeat replies to the repeating groove'
                                  : compStyle === 'beat-two'
                                    ? 'How often the chords answer on beat 2; silent bars stay open'
                                    : compStyle === 'interactive'
                                      ? 'How busy the comping is (choruses build on top of this)'
                                      : compStyle === 'sparse'
                                        ? 'How often a push or re-statement decorates the held chords'
                                        : 'How often chromatic approach stabs fire'
                              }
                              style={{ width: 90 }}
                            />
                            <span style={{ fontVariantNumeric: 'tabular-nums', minWidth: 34 }}>
                              {Math.round(compAmount * 100)}%
                            </span>
                          </div>
                        )}
                        <div className="mixer-quick-field">
                          <span className="label" style={{ textTransform: 'none' }}>
                            Placement
                          </span>
                          <SegmentedChoice
                            label="Comp placement"
                            value={varies.has('compPlacement') ? null : compPlacement}
                            options={PLACEMENT_CHOICES}
                            onChange={(value) => patchBand({ compPlacement: value })}
                            testid="comp-placement"
                          />
                        </div>
                        {partSwingField('comp', compSwingOffset)}
                        <div className="band-channel-details">
                          <div className="mixer-controls">
                            {compInstrument === 'guitar' && (
                              <>
                                <label
                                  className="label"
                                  style={{ textTransform: 'none' }}
                                  htmlFor="comp-guitar-sound"
                                >
                                  Guitar sound
                                </label>
                                <select
                                  id="comp-guitar-sound"
                                  className="bass-style-select"
                                  value={guitarSound}
                                  onChange={(e) => setGuitarSound(e.target.value as GuitarSound)}
                                  data-testid="comp-guitar-sound"
                                >
                                  <option value="jazz-guitar">Jazz guitar</option>
                                  <option value="original">Original guitar</option>
                                </select>
                              </>
                            )}
                            {compInstrument !== 'guitar' && (
                              <>
                                <span
                                  className="label"
                                  style={{ textTransform: 'none' }}
                                  title="Which voicing family the piano plays — the voicings sheet follows it"
                                >
                                  Voicings
                                </span>
                                <select
                                  className="bass-style-select"
                                  value={
                                    varies.has('compPianoFamily') || varies.has('compInstrument')
                                      ? ''
                                      : effectivePianoFamily(compInstrument, compPianoFamily)
                                  }
                                  onChange={(e) =>
                                    patchBand({
                                      compPianoFamily: e.target.value as PianoCompFamily,
                                    })
                                  }
                                  title={
                                    pianoFamilyChoices(compInstrument).find(
                                      (f) =>
                                        f.id ===
                                        effectivePianoFamily(compInstrument, compPianoFamily),
                                    )?.hint
                                  }
                                  aria-label="Piano voicing family"
                                  data-testid="comp-piano-family"
                                >
                                  <VariesOption />
                                  {pianoFamilyChoices(compInstrument).map((f) => (
                                    <option key={f.id} value={f.id} title={f.hint}>
                                      {f.label}
                                    </option>
                                  ))}
                                </select>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </MixerSettings>
                <MixerSettings channel="bass">
                  <div className="mixer-channel mixer-instrument-settings">
                    <label
                      className="switch mixer-toggle"
                      title={
                        bassHasSource
                          ? 'Generate a walking bass line under the chords'
                          : 'This tune has no chords, so there is no line to walk'
                      }
                    >
                      <input
                        type="checkbox"
                        checked={bassline}
                        disabled={!bassHasSource}
                        onChange={(e) => patchBand({ bassline: e.target.checked })}
                        data-testid="bassline"
                      />
                      Bass
                    </label>
                    {bassHasSource && (
                      <SoundPicker
                        quick
                        label="Bass sound"
                        value={bassSound}
                        options={BASS_SOUNDS}
                        onChange={setBassSound}
                        testid="mixer-quick-bass"
                      />
                    )}
                    {!bassHasSource && (
                      <div className="mixer-controls">
                        <span
                          className="label"
                          style={{ textTransform: 'none', opacity: 0.6 }}
                          data-testid="bass-no-source"
                        >
                          No chords to walk
                        </span>
                      </div>
                    )}
                    {bassHasSource && (
                      <div className="mixer-quick-controls" data-testid="bass-group">
                        <div className="mixer-quick-field">
                          <span className="label" style={{ textTransform: 'none' }}>
                            Style
                          </span>
                          <select
                            className="bass-style-select"
                            value={varies.has('bassStyle') ? '' : bassStyle}
                            onChange={(e) =>
                              patchBand({ bassStyle: e.target.value as typeof bassStyle })
                            }
                            title={BASS_STYLES.find((s) => s.id === bassStyle)?.hint}
                            aria-label="Walking bass style"
                            data-testid="bass-style"
                          >
                            <VariesOption />
                            {BASS_STYLES.map((s) => (
                              <option key={s.id} value={s.id}>
                                {s.label}
                              </option>
                            ))}
                          </select>
                        </div>
                        {(varies.has('bassStyle') ||
                          (bassStyle !== 'shuffle' && bassStyle !== 'latin')) && (
                          <div className="mixer-quick-field">
                            <span className="label" style={{ textTransform: 'none' }}>
                              Feel
                            </span>
                            <SegmentedChoice
                              label="Bass feel"
                              value={varies.has('bassFeel') ? null : bassFeel}
                              options={[
                                { id: 'two', label: '2' },
                                { id: 'four', label: '4' },
                              ]}
                              onChange={(value) => patchBand({ bassFeel: value })}
                              testid="bass-feel"
                            />
                            {varies.has('bassFeel') && (
                              <span className="band-channel-note">Varies</span>
                            )}
                          </div>
                        )}
                        <div className="mixer-quick-field">
                          <span className="label" style={{ textTransform: 'none' }}>
                            Placement
                          </span>
                          <SegmentedChoice
                            label="Bass placement"
                            value={varies.has('bassPlacement') ? null : bassPlacement}
                            options={PLACEMENT_CHOICES}
                            onChange={(value) => patchBand({ bassPlacement: value })}
                            testid="bass-placement"
                          />
                        </div>
                        {partSwingField('bass', bassSwingOffset)}
                        <div className="mixer-quick-field">
                          <span className="label" style={{ textTransform: 'none' }}>
                            Accent
                          </span>
                          <SegmentedChoice
                            label="Bass accent"
                            value={varies.has('bassAccent') ? null : bassAccent}
                            options={ACCENT_CHOICES}
                            onChange={(value) => patchBand({ bassAccent: value })}
                            testid="bass-accent"
                          />
                        </div>
                        <div className="band-channel-details">
                          <div className="mixer-controls">
                            {arrangementPolicy === 'head-solos-head' && (
                              <label
                                className="switch"
                                title="The rhythm-section convention, automated: play the heads in 2 and walk the solo choruses in 4, overriding the feel switch per pass"
                              >
                                <input
                                  type="checkbox"
                                  checked={bassFollowForm}
                                  onChange={(e) => patchBand({ bassFollowForm: e.target.checked })}
                                  data-testid="bass-follow-form"
                                />
                                Heads in 2
                              </label>
                            )}
                            <label
                              className="switch"
                              title="Add ghosted eighth-note skips into the targets"
                            >
                              <input
                                type="checkbox"
                                checked={bassGhosts}
                                onChange={(e) => patchBand({ bassGhosts: e.target.checked })}
                                data-testid="bass-ghosts"
                              />
                              Ghost notes
                            </label>
                            <label
                              className="switch"
                              title="Push some chord-change downbeats an eighth early"
                            >
                              <input
                                type="checkbox"
                                checked={bassAnticipate}
                                onChange={(e) => patchBand({ bassAnticipate: e.target.checked })}
                                data-testid="bass-anticipate"
                              />
                              Anticipate
                            </label>
                            <label
                              className="switch"
                              title="Roll a triplet 'drop' into some targets"
                            >
                              <input
                                type="checkbox"
                                checked={bassTriplets}
                                onChange={(e) => patchBand({ bassTriplets: e.target.checked })}
                                data-testid="bass-triplets"
                              />
                              Triplets
                            </label>
                            <label
                              className="switch"
                              title="Pluck the eighth before some beats, repeating a note"
                            >
                              <input
                                type="checkbox"
                                checked={bassSkips}
                                onChange={(e) => patchBand({ bassSkips: e.target.checked })}
                                data-testid="bass-skips"
                              />
                              Skips
                            </label>
                            {(bassGhosts || bassAnticipate || bassTriplets || bassSkips) && (
                              <div
                                className="control-group"
                                style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}
                              >
                                <span
                                  className="label"
                                  style={{ textTransform: 'none' }}
                                  title="How often the rhythmic embellishments fire"
                                >
                                  Variation
                                </span>
                                <input
                                  type="range"
                                  min={0}
                                  max={RATE_SLIDER_MAX}
                                  step={1}
                                  value={rateToSlider(bassAmount)}
                                  onChange={(e) =>
                                    patchBand({ bassAmount: sliderToRate(Number(e.target.value)) })
                                  }
                                  aria-label="Embellishment variation"
                                  aria-valuetext={formatRate(bassAmount)}
                                  data-testid="bass-amount"
                                  style={{ width: 110 }}
                                />
                                <span style={{ fontVariantNumeric: 'tabular-nums', minWidth: 34 }}>
                                  {formatRate(bassAmount)}
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </MixerSettings>
                <MixerSettings channel="drums">
                  <div className="mixer-channel mixer-instrument-settings">
                    <label
                      className="switch mixer-toggle"
                      title="Play a real drum loop at the chosen tempo"
                    >
                      <input
                        type="checkbox"
                        checked={drums}
                        onChange={(e) => patchBand({ drums: e.target.checked })}
                        data-testid="drums"
                      />
                      Drums
                    </label>
                    <div className="mixer-quick-controls" data-testid="drum-group">
                      <div className="mixer-quick-field mixer-drum-loop">
                        <span className="label" style={{ textTransform: 'none' }}>
                          Loop
                        </span>
                        <DrumLoopMenu
                          loops={meterDrumLoops}
                          value={varies.has('drumLoopKey') ? null : (activeDrumLoop?.key ?? null)}
                          placeholder={varies.has('drumLoopKey') ? 'Varies' : undefined}
                          onChange={(key) => key && patchBand({ drumLoopKey: key })}
                          testid="mixer-quick-drums"
                          disabled={meterDrumLoops.length === 0}
                        />
                      </div>
                      <div className="band-channel-details">
                        <div className="mixer-controls">{drumLoopSettings(true)}</div>
                      </div>
                    </div>
                  </div>
                </MixerSettings>
                <MixerSettings channel="click">
                  <div className="mixer-channel" data-testid="click-channel">
                    <label className="switch mixer-toggle">
                      <input
                        type="checkbox"
                        checked={metronome}
                        onChange={(e) => setMetronome(e.target.checked)}
                        data-testid="click-channel-toggle"
                      />
                      Click
                    </label>
                    <LevelFader fader="click" label="Click" />
                    <SoundPicker
                      caption="Sound"
                      label="Click sound"
                      value={metronomeSettings.sound}
                      options={CLICK_SOUND_CHOICES}
                      onChange={(sound) => patchMetronome({ sound })}
                      testid="click-sound"
                    />
                    <div className="band-channel-details">
                      <div className="mixer-controls">
                        <div className="band-choice-row">
                          <span className="label">Beats</span>
                          <SegmentedChoice
                            label="Click beats"
                            value={metronomeSettings.accents}
                            options={CLICK_ACCENT_CHOICES}
                            onChange={(accents) => patchMetronome({ accents })}
                            testid="click-accents"
                          />
                        </div>
                        <div className="band-choice-row">
                          <span className="label">Fill</span>
                          <SegmentedChoice
                            label="Click subdivision"
                            value={metronomeSettings.subdivision}
                            options={CLICK_SUBDIVISION_CHOICES}
                            onChange={(subdivision) => patchMetronome({ subdivision })}
                            testid="click-subdivision"
                          />
                        </div>
                        <div className="band-choice-row">
                          <span className="label">Effects</span>
                          <SegmentedChoice
                            label="Click routing"
                            value={metronomeSettings.routing}
                            options={CLICK_ROUTING_CHOICES}
                            onChange={(routing) => patchMetronome({ routing })}
                            testid="click-routing"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </MixerSettings>
              </>
            }
            effects={
              <MixerSettings channel="master">
                {/* Master effects colour the whole output — every channel and a
                tapped grip alike — so they sit below the faders in Mixer. */}
                <MasterEffectControl
                  effect="compression"
                  label="Compression"
                  amount={masterCompression}
                  enabled={masterCompressionEnabled}
                  onAmountChange={setMasterCompression}
                  onEnabledChange={setMasterCompressionEnabled}
                />
                <MasterEffectControl
                  effect="reverb"
                  label="Reverb"
                  amount={masterReverb}
                  enabled={masterReverbEnabled}
                  onAmountChange={setMasterReverb}
                  onEnabledChange={setMasterReverbEnabled}
                />
              </MixerSettings>
            }
          />
        ),
        about: (close) => <TuneAbout chart={chart} onNavigate={close} />,
      })}
    />
  );
}

/** A select's value while the bands it edits disagree: shown, never chosen. */
function VariesOption() {
  return (
    <option value="" disabled hidden>
      Varies
    </option>
  );
}

/** The family list an instrument offers: the left hand its one-hand vocabulary, the
 * two-handed piano the comping textures. */
function pianoFamilyChoices(instrument: CompInstrument) {
  return instrument === 'piano-left' ? PIANO_LEFT_HAND_FAMILIES : PIANO_COMP_FAMILIES;
}

/** Whole-band lineups: one tap sets who plays the head, the comp and the bass. */
const BAND_LINEUPS: readonly {
  id: string;
  label: string;
  lead: LeadSound;
  comp: CompInstrument;
  bass: BassSound;
}[] = [
  { id: 'piano-trio', label: 'Piano trio', lead: 'piano', comp: 'piano-left', bass: 'upright' },
  { id: 'quartet', label: 'Quartet', lead: 'tenor-sax', comp: 'piano', bass: 'upright' },
  { id: 'guitar-trio', label: 'Guitar trio', lead: 'guitar', comp: 'guitar', bass: 'upright' },
];

/** A channel's instrument, one tap away on the channel row (#416). */
function SoundPicker<T extends string>(props: {
  label: string;
  /** A visible label beside a choice of sounds (the click's "Sound"). */
  caption?: string;
  value: T;
  options: readonly { id: T; label: string }[];
  onChange: (value: T) => void;
  testid: string;
  quick?: boolean;
  disabled?: boolean;
  displayValue?: string;
}) {
  const { quick, disabled, displayValue, caption, ...choices } = props;
  if (quick)
    return (
      <div className="mixer-sound">
        <span className="label">Instrument</span>
        <MixerSoundControl
          sound={
            displayValue ??
            props.options.find((option) => option.id === props.value)?.label ??
            'Varies'
          }
          label={props.label}
          value={props.value}
          options={props.options}
          onChange={(value) => props.onChange(value as T)}
          disabled={disabled}
          testid={props.testid}
        />
      </div>
    );
  return (
    <div className={caption ? 'mixer-sound band-choice-row' : 'mixer-sound'}>
      {caption && <span className="label">{caption}</span>}
      <SegmentedChoice {...choices} disabled={disabled} />
    </div>
  );
}

/** Bypass remembers the amount; dragging the slider makes that amount audible. */
function MasterEffectControl({
  effect,
  label,
  amount,
  enabled,
  onAmountChange,
  onEnabledChange,
}: {
  effect: 'compression' | 'reverb';
  label: string;
  amount: number;
  enabled: boolean;
  onAmountChange: (amount: number) => void;
  onEnabledChange: (enabled: boolean) => void;
}) {
  const id = `master-${effect}`;
  const percent = Math.round(amount * 100);
  return (
    <div className="mixer-channel">
      <label className="mixer-toggle" htmlFor={id}>
        {label}
      </label>
      <div className="mixer-level mixer-effect-level">
        <input
          id={id}
          type="range"
          min={0}
          max={100}
          step={5}
          value={percent}
          onChange={(e) => onAmountChange(Number(e.target.value) / 100)}
          aria-valuetext={percent === 0 ? 'Off' : `${percent}%${enabled ? '' : ' (off)'}`}
          data-testid={id}
        />
        <span>{percent}%</span>
      </div>
      <label className="switch" title={`Turn ${label.toLowerCase()} on or off; keep its amount`}>
        <input
          type="checkbox"
          checked={enabled}
          onChange={(e) => onEnabledChange(e.target.checked)}
          aria-label={`${label} enabled`}
          data-testid={`${id}-enabled`}
        />
        {enabled ? 'On' : 'Off'}
      </label>
    </div>
  );
}

const FEEL_CHOICES: readonly { id: FeelTiming; label: string }[] = [
  { id: 'tight', label: 'Tight' },
  { id: 'relaxed', label: 'Relaxed' },
  { id: 'loose', label: 'Loose' },
];

const PLACEMENT_CHOICES: readonly { id: Placement; label: string }[] = [
  { id: 'on-top', label: 'On top' },
  { id: 'centre', label: 'Centre' },
  { id: 'behind', label: 'Behind' },
];

const PHRASING_CHOICES: readonly { id: MelodyPhrasing; label: string }[] = [
  { id: 'written', label: 'As written' },
  { id: 'swing', label: 'Swing' },
];

const ACCENT_CHOICES: readonly { id: BassAccent; label: string }[] = [
  { id: 'even', label: 'Even' },
  { id: 'two-four', label: '2 and 4' },
  { id: 'one-three', label: '1 and 3' },
];

/** The swing picker's state: null while the bands being edited disagree. */
interface SwingControl {
  choice: SwingChoice | null;
  onChange: (choice: SwingChoice) => void;
  scope?: ReactNode;
  /** What the Kit choice swings at, or null. */
  kitStatus?: string | null;
  /** The band's swing against its kit, a slider under Kit, or null. */
  amount?: SwingAmount | null;
}

/** Each part's swing offset field, and its name on the slider. */
const SWING_OFFSET_FIELD = {
  melody: 'melodySwingOffset',
  comp: 'compSwingOffset',
  bass: 'bassSwingOffset',
} as const satisfies Record<SwingPart, keyof StyleBand>;
const SWING_PART_NAMES: Record<SwingPart, string> = {
  melody: 'Melody',
  comp: 'Comp',
  bass: 'Bass',
};

/**
 * How the band swings and where it sits against the beat (#423). The swing
 * is this tune's (a band field, through the band editor and its scope); the
 * timing is the player's, for every tune.
 */
function FeelControls({ swing }: { swing: SwingControl }) {
  const feelTiming = useStore((s) => s.feelTiming);
  const setFeelTiming = useStore((s) => s.setFeelTiming);
  return (
    <section className="practice-feel" aria-labelledby="practice-feel-title">
      <h3 className="practice-section-title" id="practice-feel-title">
        Feel
      </h3>
      {swing.scope}
      <SwingFeelPicker
        value={swing.choice}
        onChange={swing.onChange}
        kitStatus={swing.kitStatus}
        amount={swing.amount}
      />
      <div className="practice-feel-row">
        <span className="label">Timing</span>
        <SegmentedChoice
          label="Band timing"
          value={feelTiming}
          options={FEEL_CHOICES}
          onChange={setFeelTiming}
          testid="feel-timing"
        />
      </div>
    </section>
  );
}

/** Assemble the transport's drawer panels: three summary chips (tempo, repeats,
 * style) plus the mixer icon. Kept out of the JSX so the render stays readable
 * and each panel's controls live next to its chip. */
/**
 * The Feel-cue picker: how THIS tune realizes each written cue.
 *
 * Appears for written cues or a supported tune arrangement: a row per feel naming
 * the band it plays (yours, the house default's, the catalogue's, or the tune's)
 * and a ✕ back to the default. A feel's band is set with the band controls
 * themselves, the scope row picking which feel they edit (components/bandScope.ts);
 * what they write is a COPY, so a later preset edit cannot reach a tune already
 * arranged.
 */
export function FeelCuePicker() {
  const chart = useStore((s) => s.chart);
  const score = useStore((s) => s.score);
  const scoreId = score.id;
  const follow = useStore((s) => s.followFeelCues);
  const assign = useStore((s) => s.assignFeelGroove);
  const currentTuneKey = useStore((s) => s.currentTuneKey);
  const allTuneFeels = useStore((s) => s.tuneFeels);
  const houseFeels = useStore((s) =>
    s.currentTuneKey ? s.houseDefaults[s.currentTuneKey]?.settings.feels : undefined,
  );
  const liveCompStyle = useStore((s) => s.compStyle);
  // Sections, when on, play in place of the written feels (sectionPlan.ts).
  const sectionsOn = useStore((s) => !!tuneSectionsOf(s).on);

  const feels = useMemo(() => feelsInScore(chart.measures), [chart.measures]);
  // The shipped default's own name, so a row can say what is ACTUALLY in
  // force. `from` is display-only provenance on the copied snapshot — this
  // is exactly what it exists for.
  const defaults = useMemo(() => catalogueArrangementFor(scoreId)?.feels ?? {}, [scoreId]);
  const hasCompPattern = useMemo(() => hasCatalogueCompPattern(score), [score]);
  // Following is chosen in BandChangesPicker above; these are its rows.
  if ((!feels.length && !hasCompPattern) || sectionsOn || !follow) return null;
  const assigned = (currentTuneKey && allTuneFeels[currentTuneKey]) || {};

  return (
    <div className="feel-cue-picker" data-testid="feel-cues">
      {hasCompPattern && (
        <p className="panel-hint" data-testid="comp-pattern-hint">
          {liveCompStyle === 'interactive'
            ? 'Interactive uses Beat-2 answers in this tune’s arranged passage.'
            : 'Choose Interactive rhythm to use this tune’s Beat-2 answers.'}
        </p>
      )}
      {feels.map((feel) => {
        const has = !!assigned[feel];
        // Precedence, said out loud: the user's own assignment, then the
        // catalogue's curated default, then the live band. Reporting "uses
        // current band" for a tune that ships an arrangement was simply
        // untrue, and it made Assign look like it had done nothing.
        const house = houseFeels?.[feel];
        const fallback = house ? matchLabel(house) : defaults[feel]?.from?.name;
        return (
          <div
            key={feel}
            className={`feel-cue-row${has ? ' is-assigned' : ''}`}
            data-testid={`feel-row-${feel}`}
          >
            <span className="feel-cue-name">{feel}</span>
            <span className="feel-cue-state muted" data-testid={`feel-state-${feel}`}>
              {has ? matchLabel(assigned[feel]) : (fallback ?? 'Tune’s band')}
            </span>
            {has && (
              <TransportControlButton
                variant="preset"
                onClick={() => assign(feel)}
                data-testid={`feel-clear-${feel}`}
                aria-label={`Clear ${feel}`}
                title={fallback ? `Back to ${fallback}` : 'Back to the tune’s band'}
              >
                ✕
              </TransportControlButton>
            )}
          </div>
        );
      })}
    </div>
  );
}

type BandSource = 'one' | 'written' | 'sections';

/**
 * Where the band's changes come from, as ONE choice (Sep 2026, user: "it looks
 * untidy with a toggle and a check box"): one band for the whole tune, the
 * feels the score writes (or a tune's shipped arrangement), or the tune's
 * sections (sectionPlan.ts). They were a switch and a checkbox for what is one
 * three-way choice, and both could read as on at once, sections silently
 * winning. The rows for the choice sit under it. Following written feels stays
 * the app-wide setting it always was; sections are this tune's.
 */
export function BandChangesPicker() {
  const measures = useStore((s) => s.chart.measures);
  const score = useStore((s) => s.score);
  const follow = useStore((s) => s.followFeelCues);
  const setFollow = useStore((s) => s.setFollowFeelCues);
  const sectionsOn = useStore((s) => !!tuneSectionsOf(s).on);
  const setSectionsOn = useStore((s) => s.setSectionsOn);
  const hasFeels = useMemo(() => feelsInScore(measures).length > 0, [measures]);
  const hasCompPattern = useMemo(() => hasCatalogueCompPattern(score), [score]);
  if (!measures.length) return null;

  const written = hasFeels || hasCompPattern;
  const source: BandSource = sectionsOn ? 'sections' : written && follow ? 'written' : 'one';
  const options: [BandSource, string][] = [
    ['one', 'One band'],
    ...(written
      ? [['written', hasCompPattern ? 'Arrangement' : 'Written feels'] as [BandSource, string]]
      : []),
    ['sections', 'By section'],
  ];
  const choose = (next: BandSource) => {
    if (next === source) return;
    if (next === 'sections') {
      setSectionsOn(true);
      return;
    }
    if (sectionsOn) setSectionsOn(false);
    if (written) setFollow(next === 'written');
  };

  return (
    <div className="band-changes" data-testid="band-changes">
      <div className="segmented panel-segmented" role="group" aria-label="Band changes">
        {options.map(([id, label]) => (
          <button
            key={id}
            type="button"
            className={source === id ? 'active' : ''}
            aria-pressed={source === id}
            data-testid={`band-source-${id}`}
            onClick={() => choose(id)}
          >
            {label}
          </button>
        ))}
      </div>
      {source === 'written' && <FeelCuePicker />}
      {source === 'sections' && <SectionPicker />}
    </div>
  );
}

/** A stored band's nearest preset name, for a readable row. */
function matchLabel(band: StyleBand): string {
  const preset = describeBandPreset(band);
  return preset ? `${preset.label}${preset.modified ? STYLE_MODIFIED : ''}` : 'Custom';
}

// iReal's transposition spellings: minor keys take the sharp side for
// C♯/F♯/G♯ and flats for B♭/E♭; major keys read D♭/A♭. Twelve buttons from A,
// exactly the book's sheet.
const KEY_GRID_MAJOR = ['A', 'Bb', 'B', 'C', 'Db', 'D', 'Eb', 'E', 'F', 'F#', 'G', 'Ab'];
const KEY_GRID_MINOR = ['A', 'Bb', 'B', 'C', 'C#', 'D', 'Eb', 'E', 'F', 'F#', 'G', 'G#'];

/** Same mode test the tune-details form uses — key labels arrive as "C",
 * "E minor", or an importer's "Dm". */
function parsedKey(label: string | undefined): { tonic: string; minor: boolean } {
  const tonic = /^([A-G][#b]?)/.exec((label ?? '').trim())?.[1] ?? 'C';
  const minor = /^.*(minor|min|m)$/i.test((label ?? '').trim());
  return { tonic, minor };
}

function prettyTonic(tonic: string): string {
  return tonic.replace('b', '♭').replace('#', '♯');
}

/**
 * How the octave displacement reads to a player: "8vb" for one octave down,
 * "8va" for one up, "2 × 8va" beyond that — the notation they already know
 * rather than a signed number.
 */
function octaveShiftLabel(shift: number): string | null {
  if (!shift) return null;
  const name = shift < 0 ? '8vb' : '8va';
  return Math.abs(shift) === 1 ? name : `${Math.abs(shift)} × ${name}`;
}

function keyChipValue(label: string | undefined): string {
  const { tonic, minor } = parsedKey(label);
  return `${prettyTonic(tonic)}${minor ? 'm' : ''}`;
}

function keyGridEntries(
  label: string | undefined,
): { tonic: string; label: string; current: boolean }[] {
  const { tonic, minor } = parsedKey(label);
  let currentPc = -1;
  try {
    currentPc = pitchClassOf(tonic);
  } catch {
    currentPc = -1;
  }
  return (minor ? KEY_GRID_MINOR : KEY_GRID_MAJOR).map((t) => ({
    tonic: t,
    label: `${prettyTonic(t)}${minor ? '–' : ''}`,
    current: pitchClassOf(t) === currentPc,
  }));
}

function buildTransportPanels(opts: {
  tempoDenominator: number;
  tempo: number;
  setTempo: (n: number) => void;
  tempoLabel: string;
  activePct: number;
  effectiveTempo: number;
  previewPlayback: boolean;
  speedPct: number;
  setSpeedPct: (n: number) => void;
  trainer: TrainerConfig;
  patchTrainer: (patch: Partial<TrainerConfig>) => void;
  countInBars: number;
  setCountInBars: (n: number) => void;
  countInBetweenLoops: boolean;
  setCountInBetweenLoops: (v: boolean) => void;
  repeatCount: number;
  repeatForm: boolean;
  setRepeatForm: (b: boolean) => void;
  setRepeats: (n: number) => void;
  arrangementPolicy: ArrangementPolicy;
  setArrangementPolicy: (p: ArrangementPolicy) => void;
  /** Whether the tune carries a written melody — see the itinerary hint. */
  hasMelody: boolean;
  /** Written intro/verse bars playing once on the way in (0 = none). */
  entryBars: number;
  /** Written coda/outro bars playing once on the way out (0 = none). */
  exitBars: number;
  /** How the last pass finishes (ritardando / tag / held chord). */
  ending: EndingOptions;
  patchEnding: (patch: Partial<EndingOptions>) => void;
  /** False under ∞, a passage loop or form cycling — playbacks with no end. */
  endingAvailable: boolean;
  currentPass: number;
  /** A chorus jump waiting for the barline — the chip names where it goes. */
  pendingChorus: number | null;
  /** Chorus names for the jump row, or null where there is no row. */
  choruses: string[] | null;
  repeatTotal: number | string;
  currentStyleId: StyleId | null;
  currentStyleLabel: string;
  /** The ticked preset's details differ from the preset's own. */
  styleModified: boolean;
  /** The bands the scope reaches disagree on the groove, so no preset is ticked. */
  stylesVary: boolean;
  /** The scope row, shown above the presets while the band is split. */
  styleScope: ReactNode;
  applyStyle: (id: StyleId) => void;
  /** The tune's written key label ("C", "E minor") — drives the key chip. */
  keyLabel: string | undefined;
  transposeToKey: (tonic: string) => void;
  shiftOctave: (octaves: number) => void;
  octaveShift: number;
  /** The active reharm layer's name, or null for the original harmony. */
  reharmLayerName: string | null;
  /** The reading edition (C treble / B♭ / E♭ / C bass) and whether this
   *  surface honours it — the studio shows the row disabled. */
  scoreEdition: ScoreEdition;
  setScoreEdition: (edition: ScoreEdition) => void;
  readingEditions: boolean;
  bandAndMixer: (panel: 'band' | 'mixer') => ReactNode;
  /** The drum loop's picker and native tempo, while the band has drums on. */
  drums: { controls: React.ReactNode; loopBpm: number | null } | null;
  swing: SwingControl;
  /** Rendered with the drawer's own close fn so opening a related tune dismisses it. */
  about: (close: () => void) => React.ReactNode;
}): TransportPanel[] {
  const {
    tempo,
    setTempo,
    tempoLabel,
    activePct,
    effectiveTempo,
    previewPlayback,
    speedPct,
    setSpeedPct,
    trainer,
    patchTrainer,
    countInBars,
    setCountInBars,
    countInBetweenLoops,
    setCountInBetweenLoops,
    repeatCount,
    repeatForm,
    setRepeatForm,
    setRepeats,
    arrangementPolicy,
    setArrangementPolicy,
    hasMelody,
    entryBars,
    exitBars,
    ending,
    patchEnding,
    endingAvailable,
    currentPass,
    pendingChorus,
    choruses,
    repeatTotal,
    currentStyleId,
    currentStyleLabel,
    styleModified,
    stylesVary,
    styleScope,
    applyStyle,
    keyLabel,
    transposeToKey,
    shiftOctave,
    octaveShift,
    reharmLayerName,
    scoreEdition,
    setScoreEdition,
    readingEditions,
    bandAndMixer,
    drums,
    swing,
    about,
  } = opts;
  const editionDef = scoreEditionDef(scoreEdition);
  const headOut = arrangementPolicy === 'head-solos-head';
  const trainerMinimumRepeats = trainer.enabled ? trainerPassesToTarget(trainer) : 1;
  const trainerActive = trainer.enabled && !previewPlayback;
  const repeatSliderMax = Math.max(16, trainerMinimumRepeats, repeatCount);
  // The note is its own span with a little room after it: Bravura's half
  // note sits flush against whatever follows, so "=" ran into it.
  const tempoBeat =
    opts.tempoDenominator === 2 ? (
      <span className="tempo-beat" style={{ fontFamily: 'Bravura' }} aria-hidden="true">
        {'\uECA3'}
      </span>
    ) : (
      <span className="tempo-beat" aria-hidden="true">
        {opts.tempoDenominator === 8 ? '♪' : '♩'}
      </span>
    );
  const panels: TransportPanel[] = [
    {
      id: 'tempo',
      title: 'Tempo',
      chip: {
        value: (
          <>
            {tempoBeat}={effectiveTempo}
          </>
        ),
        caption: 'tempo',
        testId: 'tempo-chip',
        ariaLabel: `Tempo ${effectiveTempo} BPM`,
      },
      content: (
        <div className="panel tempo-panel" data-testid="tempo-panel">
          <p className="panel-bignum">
            {effectiveTempo}
            <small>bpm</small>
          </p>
          <p className="panel-hint">
            Playing at {activePct}% of the {tempoLabel.toLowerCase()}, {tempoBeat}={tempo}
          </p>
          <TempoStepper id="transport-tempo" label={tempoLabel} tempo={tempo} setTempo={setTempo} />
          <FeelControls swing={swing} />
          {drums && (
            <section className="practice-drums" aria-labelledby="practice-drums-title">
              <h3 className="practice-section-title" id="practice-drums-title">
                Drums
              </h3>
              {drums.loopBpm !== null && (
                <DrumLoopRate
                  loopBpm={drums.loopBpm}
                  effectiveTempo={effectiveTempo}
                  activePct={activePct}
                  disabled={trainerActive}
                  setTempo={setTempo}
                />
              )}
              <div className="mixer-controls">{drums.controls}</div>
            </section>
          )}
          <p className="tempo-field-label">Practice speed · {activePct}%</p>
          <div className="practice-presets" role="group" aria-label="Speed presets">
            {[50, 60, 70, 80, 90, 100, 125].map((pct) => (
              <TransportControlButton
                key={pct}
                variant="preset"
                active={!trainerActive && speedPct === pct}
                disabled={trainerActive}
                aria-pressed={!trainerActive && speedPct === pct}
                data-testid={`speed-${pct}`}
                onClick={() => setSpeedPct(pct)}
              >
                {pct}%
              </TransportControlButton>
            ))}
          </div>
          <input
            type="range"
            className="panel-slider"
            min={25}
            max={150}
            step={5}
            value={speedPct}
            disabled={trainerActive}
            onChange={(e) => setSpeedPct(Number(e.target.value))}
            aria-label="Practice speed (%)"
          />

          {!previewPlayback && (
            <section className="practice-trainer" aria-labelledby="practice-trainer-title">
              <h3 className="practice-section-title" id="practice-trainer-title">
                Speed trainer{trainer.enabled ? ` · ${trainer.fromPct}→${trainer.toPct}%` : ''}
              </h3>
              <label className="switch practice-switch">
                <span>
                  <strong>Enable speed trainer</strong>
                  <small>Step the tempo up each pass</small>
                </span>
                <input
                  type="checkbox"
                  data-testid="speed-trainer"
                  checked={trainer.enabled}
                  onChange={(e) => patchTrainer({ enabled: e.target.checked })}
                />
              </label>
              {trainer.enabled && (
                <>
                  <div className="practice-grid">
                    <FormField label="From %">
                      <NumberField
                        min={25}
                        max={150}
                        value={trainer.fromPct}
                        data-testid="trainer-from"
                        onCommit={(n) => patchTrainer({ fromPct: n })}
                      />
                    </FormField>
                    <FormField label="To %">
                      <NumberField
                        min={25}
                        max={150}
                        value={trainer.toPct}
                        data-testid="trainer-to"
                        onCommit={(n) => patchTrainer({ toPct: n })}
                      />
                    </FormField>
                    <FormField label="Step %">
                      <NumberField
                        min={1}
                        max={50}
                        value={trainer.stepPct}
                        data-testid="trainer-step"
                        onCommit={(n) => patchTrainer({ stepPct: n })}
                      />
                    </FormField>
                    <FormField label="Passes/step">
                      <NumberField
                        min={1}
                        max={8}
                        value={trainer.passesPerStep}
                        data-testid="trainer-passes"
                        onCommit={(n) => patchTrainer({ passesPerStep: n })}
                      />
                    </FormField>
                  </div>
                  <p className="panel-hint" data-testid="trainer-schedule-hint">
                    {trainerMinimumRepeats} form pass
                    {trainerMinimumRepeats === 1 ? '' : 'es'} to reach {trainer.toPct}%. Repeats
                    stay at least ×{trainerMinimumRepeats}.
                  </p>
                </>
              )}
            </section>
          )}

          {!previewPlayback && (
            <section className="practice-countin" aria-labelledby="practice-countin-title">
              <h3 className="practice-section-title" id="practice-countin-title">
                Count-in
              </h3>
              <div className="panel-stepper">
                <TransportControlButton
                  aria-label="Shorter count-in"
                  onClick={() => setCountInBars(countInBars - 1)}
                >
                  −
                </TransportControlButton>
                <span data-testid="count-in-bars">
                  {countInBars} bar{countInBars > 1 ? 's' : ''}
                </span>
                <TransportControlButton
                  aria-label="Longer count-in"
                  onClick={() => setCountInBars(countInBars + 1)}
                >
                  +
                </TransportControlButton>
              </div>
              <label className="switch practice-switch">
                <span>Count-in between loops</span>
                <input
                  type="checkbox"
                  data-testid="count-in-between"
                  checked={countInBetweenLoops}
                  onChange={(e) => setCountInBetweenLoops(e.target.checked)}
                />
              </label>
              <p className="panel-hint">Count-in length applies from the next Play.</p>
            </section>
          )}
        </div>
      ),
    },
    {
      id: 'repeats',
      title: headOut ? 'Choruses' : 'Repeats',
      chip: {
        value:
          currentPass > 0
            ? pendingChorus === null
              ? `${currentPass}/${repeatTotal}`
              : pendingChorus === currentPass
                ? `↺ ${currentPass}`
                : `${currentPass}→${pendingChorus}`
            : repeatForm
              ? '∞'
              : `×${repeatCount}`,
        caption: headOut ? 'choruses' : 'repeats',
        testId: 'repeat-chip',
        ariaLabel: `Repeats: ${repeatForm ? 'infinite' : repeatCount}`,
      },
      content: (
        <div className="panel">
          {/* How the sheet becomes a performance. "As written" obeys the
              notation once; the head-out policy plays the jazz itinerary and
              turns the count below into the number of choruses. */}
          <div className="segmented panel-segmented" role="group" aria-label="Arrangement">
            {(
              [
                ['as-written', 'As written'],
                ['head-solos-head', 'Head · solos · out'],
              ] as [ArrangementPolicy, string][]
            ).map(([id, label]) => (
              <button
                key={id}
                type="button"
                className={arrangementPolicy === id ? 'active' : ''}
                aria-pressed={arrangementPolicy === id}
                data-testid={`policy-${id}`}
                onClick={() => setArrangementPolicy(id)}
              >
                {label}
              </button>
            ))}
          </div>
          {/* Dropping the melody is the ONLY thing that separates a solo
              chorus from the head — chords, comp, bass and drums are
              generated identically from the same bars. On a chord-only chart
              the itinerary is therefore inaudible, and saying so beats
              letting somebody conclude the feature is broken (user report,
              All Of Me, July 2026). */}
          {headOut && !hasMelody && (
            <p className="panel-hint" data-testid="policy-no-melody">
              This tune has no written melody, so the solo choruses will sound the same as the head.
            </p>
          )}
          {/* Jump straight to a chorus while the band plays. It lands on
              the next barline, like the skip buttons beside Play. */}
          {choruses && currentPass > 0 && (
            <div className="practice-presets" role="group" aria-label="Go to chorus">
              {choruses.map((label, i) => {
                const pass = i + 1;
                const here = pass === currentPass;
                return (
                  <TransportControlButton
                    key={pass}
                    variant="preset"
                    active={here}
                    aria-current={here ? 'true' : undefined}
                    aria-label={`${/^\d+$/.test(label) ? `Chorus ${label}` : label}${
                      pendingChorus === pass ? ', next' : ''
                    }`}
                    data-testid="chorus-jump"
                    data-pending={pendingChorus === pass || undefined}
                    onClick={() => getChordPlayer().jumpToChorus(pass)}
                  >
                    {label}
                  </TransportControlButton>
                );
              })}
            </div>
          )}
          <p className="panel-bignum">{repeatForm ? '∞' : `×${repeatCount}`}</p>
          <div className="panel-stepper">
            <TransportControlButton
              aria-label="Fewer repeats"
              disabled={repeatCount <= trainerMinimumRepeats}
              onClick={() => setRepeats(repeatCount - 1)}
            >
              −
            </TransportControlButton>
            <TransportControlButton
              active={repeatForm}
              aria-pressed={repeatForm}
              aria-label="Loop until stop"
              data-testid="repeat-infinite"
              onClick={() => setRepeatForm(!repeatForm)}
            >
              ∞
            </TransportControlButton>
            <TransportControlButton
              aria-label="More repeats"
              disabled={repeatCount >= repeatSliderMax}
              onClick={() => setRepeats(repeatCount + 1)}
            >
              +
            </TransportControlButton>
          </div>
          <input
            type="range"
            className="panel-slider"
            min={trainerMinimumRepeats}
            max={repeatSliderMax}
            step={1}
            value={repeatCount}
            onChange={(e) => setRepeats(Number(e.target.value))}
            aria-label="Repeats"
          />
          <p className="panel-hint">
            {currentPass > 0
              ? `On ${headOut ? 'pass' : 'repeat'} ${currentPass} of ${repeatTotal}`
              : repeatForm
                ? headOut
                  ? 'Head in as written, then solo choruses until Stop'
                  : '∞ loops until you hit Stop'
                : headOut
                  ? `Head in as written, ${repeatCount} solo chorus${repeatCount === 1 ? '' : 'es'}, then the head out taking the coda`
                  : `Plays ${repeatCount}× as written, without added solo choruses`}
          </p>
          {/* A loop that quietly skips the intro reads as broken unless the
              UI names the behaviour: say what plays once and what repeats. */}
          {(entryBars > 0 || exitBars > 0) && (
            <p className="panel-hint" data-testid="loop-shape-hint">
              {[
                entryBars > 0 ? `the ${entryBars}-bar intro plays once, on the way in` : '',
                exitBars > 0 ? `the ${exitBars}-bar coda plays once, at the end` : '',
              ]
                .filter(Boolean)
                .join('; ')
                .replace(/^./, (c) => c.toUpperCase())}
              {' — repeats loop the form.'}
            </p>
          )}
          {trainer.enabled && (
            <p className="panel-hint" data-testid="trainer-repeat-floor">
              Trainer minimum: ×{trainerMinimumRepeats} to reach {trainer.toPct}%
            </p>
          )}
          {/* ENDINGS — how the last pass finishes (src/audio/endings.ts). The
              rows are disabled, not hidden, where the playback has no end (∞,
              a passage loop, form cycling): a row that only exists on the
              right setting reads as the option not existing at all. */}
          <div className="panel-endings" data-testid="ending-options">
            <p className="panel-section-title">Ending</p>
            <div className="settings-row">
              <span className="settings-label">Slow down</span>
              <div className="segmented" role="group" aria-label="Ritardando">
                {RITARDANDO_BARS.map((bars) => (
                  <button
                    key={bars}
                    type="button"
                    className={ending.ritardandoBars === bars ? 'active' : ''}
                    aria-pressed={ending.ritardandoBars === bars}
                    disabled={!endingAvailable}
                    data-testid={`ending-rit-${bars}`}
                    onClick={() => patchEnding({ ritardandoBars: bars })}
                  >
                    {bars === 0 ? 'Off' : bars === 1 ? 'Last bar' : `${bars} bars`}
                  </button>
                ))}
              </div>
            </div>
            <div className="settings-row">
              <span className="settings-label">Tag</span>
              <div className="segmented" role="group" aria-label="Tag ending">
                {TAG_BARS.map((bars) => (
                  <button
                    key={bars}
                    type="button"
                    className={ending.tagBars === bars ? 'active' : ''}
                    aria-pressed={ending.tagBars === bars}
                    disabled={!endingAvailable}
                    data-testid={`ending-tag-${bars}`}
                    onClick={() => patchEnding({ tagBars: bars })}
                  >
                    {bars === 0 ? 'Off' : `Last ${bars} bars`}
                  </button>
                ))}
              </div>
            </div>
            {ending.tagBars > 0 && (
              <div className="settings-row">
                <span className="settings-label">Tag plays</span>
                <div className="segmented" role="group" aria-label="Tag times">
                  {TAG_TIMES.map((times) => (
                    <button
                      key={times}
                      type="button"
                      className={ending.tagTimes === times ? 'active' : ''}
                      aria-pressed={ending.tagTimes === times}
                      disabled={!endingAvailable}
                      data-testid={`ending-tag-times-${times}`}
                      onClick={() => patchEnding({ tagTimes: times })}
                    >
                      {times === 2 ? 'Twice' : 'Three times'}
                    </button>
                  ))}
                </div>
              </div>
            )}
            <div className="settings-row">
              <span className="settings-label">Resolve</span>
              <div className="segmented" role="group" aria-label="Resolve the ending">
                {RESOLVE_ENDINGS.map((resolve) => (
                  <button
                    key={resolve}
                    type="button"
                    className={ending.resolve === resolve ? 'active' : ''}
                    aria-pressed={ending.resolve === resolve}
                    disabled={!endingAvailable}
                    data-testid={`ending-resolve-${resolve}`}
                    onClick={() => patchEnding({ resolve })}
                  >
                    {RESOLVE_LABELS[resolve]}
                  </button>
                ))}
              </div>
            </div>
            <div className="settings-row">
              <span className="settings-label">Hold the last chord</span>
              <ToggleSwitch
                on={ending.holdLast}
                onChange={(holdLast) => patchEnding({ holdLast })}
                label="Hold the last chord"
                disabled={!endingAvailable}
                testid="ending-hold"
              />
            </div>
          </div>
        </div>
      ),
    },
    {
      // iReal's transposition sheet: twelve keys, the current one lit. A tap
      // moves the WHOLE tune — form harmony, melody, every part — through the
      // store's native transpose, mode preserved.
      id: 'key',
      title: 'Key',
      chip: {
        value: octaveShiftLabel(octaveShift)
          ? `${keyChipValue(keyLabel)} ${octaveShiftLabel(octaveShift)}`
          : keyChipValue(keyLabel),
        caption: 'key',
        testId: 'key-chip',
        ariaLabel: octaveShiftLabel(octaveShift)
          ? `Key: ${keyLabel || 'C'}, ${octaveShiftLabel(octaveShift)}`
          : `Key: ${keyLabel || 'C'}`,
      },
      content: (
        <div className="panel">
          <div className="key-grid" role="group" aria-label="Transpose to key">
            {keyGridEntries(keyLabel).map((entry) => (
              <button
                key={entry.tonic}
                type="button"
                className={`key-cell${entry.current ? ' active' : ''}`}
                aria-pressed={entry.current}
                data-testid={`transpose-${entry.tonic}`}
                onClick={() => transposeToKey(entry.tonic)}
              >
                {entry.label}
              </button>
            ))}
          </div>
          {/* Register, beside key, because they are the two ways to move where
              a tune sits — and the one people reach for (key) cannot do this:
              `transpose(±12)` folds to zero by design, so until now nothing in
              the app moved a score up or down the stave. */}
          <div className="octave-shift" role="group" aria-label="Move the whole tune by an octave">
            <span className="label">
              Octave
              <span className="octave-state" data-testid="octave-state">
                {octaveShiftLabel(octaveShift) ?? 'as written'}
              </span>
            </span>
            {/* Reset leads the group, and that placement is load-bearing: the
                buttons are flush right, so appending it pushed 8vb and 8va
                left and dropped Reset exactly where the finger that just
                pressed 8va still was. Leading the group, it grows leftwards
                and both octave buttons stay put. One press also undoes however
                many shifts got here, which otherwise means counting them. */}
            {octaveShift !== 0 && (
              <button
                type="button"
                className="btn btn-sm btn-ghost"
                data-testid="octave-reset"
                onClick={() => shiftOctave(-octaveShift)}
              >
                Reset
              </button>
            )}
            <button
              type="button"
              className="btn btn-sm"
              data-testid="octave-down"
              onClick={() => shiftOctave(-1)}
            >
              8vb ↓
            </button>
            <button
              type="button"
              className="btn btn-sm"
              data-testid="octave-up"
              onClick={() => shiftOctave(1)}
            >
              8va ↑
            </button>
          </div>
          {/* Which BOOK the sheet is — C treble, B♭, E♭ or bass clef. A
              reading transform, never an edit: the key grid above changes the
              TUNE, this changes who can read it, and the band keeps playing
              in concert pitch — a horn player reads their own key against a
              rhythm section in the tune's. */}
          <div className="reading-edition" role="group" aria-label="Read as">
            <span className="label">
              Reading
              <span className="octave-state" data-testid="edition-state">
                {editionDef.label}
              </span>
            </span>
            {/* Its own group, so on a phone it wraps to a line of its own,
                still right-aligned under the label. */}
            <div className="reading-edition-controls">
              <div className="segmented">
                {SCORE_EDITIONS.map((edition) => (
                  <button
                    key={edition.id}
                    type="button"
                    className={scoreEdition === edition.id ? 'active' : ''}
                    aria-pressed={scoreEdition === edition.id}
                    disabled={!readingEditions}
                    title={edition.hint}
                    data-testid={`edition-${edition.id}`}
                    onClick={() => {
                      setScoreEdition(edition.id);
                      recordFeature('edition');
                    }}
                  >
                    {edition.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
          {/* Who reads this book and at what interval, and nothing else —
              right-aligned under the control it describes. */}
          <p className="panel-hint edition-hint" data-testid="edition-hint">
            {readingEditions
              ? editionDef.hint
              : 'Workbench only (the studio edits in concert pitch)'}
          </p>
        </div>
      ),
    },
    {
      // Reharm layers: which harmonization of the tune is in force. Always
      // shown — a chip that appears only once a layer exists reads as the
      // feature not existing (the Pages-row lesson).
      id: 'reharm',
      title: 'Harmony',
      chip: {
        value: reharmLayerName ?? 'Original',
        caption: 'harmony',
        testId: 'reharm-chip',
        ariaLabel: `Harmony: ${reharmLayerName ?? 'Original'}`,
      },
      content: <ReharmLayersPanel />,
    },
    {
      id: 'style',
      title: 'Style',
      chip: {
        // The qualifier is its own span: a phone's chip drops it (it cannot fit
        // beside the name there), and the panel's ticked row still says it.
        value: currentStyleLabel.endsWith(STYLE_MODIFIED) ? (
          <>
            {currentStyleLabel.slice(0, -STYLE_MODIFIED.length)}
            <span className="transport-chip-qualifier">{STYLE_MODIFIED}</span>
          </>
        ) : (
          currentStyleLabel
        ),
        caption: 'style',
        testId: 'style-chip',
        ariaLabel: `Style: ${currentStyleLabel}`,
      },
      content: (
        <div className="panel style-list" data-testid="style-preset">
          <BandChangesPicker />
          {styleScope}
          {currentStyleId === null && (
            <div
              className="transport-control-button transport-control-row is-active transport-control-static"
              data-testid={stylesVary ? 'style-varies' : undefined}
            >
              {stylesVary ? 'Varies' : 'Custom'}
              <span className="style-check">✓</span>
            </div>
          )}
          {STYLE_PRESETS.map((p) => (
            <TransportControlButton
              key={p.id}
              variant="row"
              active={currentStyleId === p.id}
              aria-current={currentStyleId === p.id}
              onClick={() => applyStyle(p.id)}
            >
              {p.label}
              {currentStyleId === p.id && styleModified && STYLE_MODIFIED}
              {currentStyleId === p.id && <span className="style-check">✓</span>}
            </TransportControlButton>
          ))}
        </div>
      ),
    },
    {
      id: 'band',
      title: 'Band',
      icon: {
        glyph: <Drum size={20} strokeWidth={1.8} aria-hidden="true" />,
        label: 'Band',
        testId: 'transport-band',
      },
      content: bandAndMixer('band'),
    },
    {
      id: 'mixer',
      title: 'Mixer',
      icon: { glyph: <MixerGlyph />, label: 'Mixer', testId: 'transport-mixer' },
      content: bandAndMixer('mixer'),
    },
    {
      // Where this tune sits in the repertoire. A drawer, not a section below the
      // page, so reading it never scrolls the score away or stops the band.
      id: 'about',
      title: 'About',
      icon: {
        glyph: <BookOpen size={20} strokeWidth={1.8} aria-hidden="true" />,
        label: 'About this tune',
        testId: 'transport-about',
      },
      content: about,
    },
  ];
  return previewPlayback
    ? panels.filter((panel) => !['repeats', 'style'].includes(panel.id))
    : panels;
}
